import { Suspense, lazy, useEffect, useState } from "react";
import Dashboard from "./pages/Dashboard";
import AdminOnboarding from "./pages/AdminOnboarding";
import AdminRevenue from "./components/modules/AdminRevenue";
import PartnerRevenueDashboard from "./components/modules/PartnerRevenueDashboard";
import Login from "./components/Login";
import StartupFallback from "./components/system/StartupFallback";
import { supabase } from "./lib/supabase";
import { buildLoginRedirect, DEFAULT_POST_LOGIN_PATH, getProtectedRouteTarget, getRedirectFromLocation } from "./lib/authRedirect";
import { getCurrentAssetIntelligenceFixtureFromLocation } from "./components/admin/currentAssetIntelligenceBrowserSafeProps";

const CurrentAssetIntelligenceBrowserSafe = lazy(() =>
  import("./components/admin/CurrentAssetIntelligenceBrowserSafe")
);
const CaiImplementationStudio = lazy(() => import("./components/admin/CaiImplementationStudio"));
function AdminRouteLoading() {
  return (
    <div style={{ maxWidth: 420, margin: "80px auto", padding: 24 }}>
        Loading browser-safe Current Asset preview...
    </div>
  );
}


function isPasswordRecoveryUrl() {
  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const queryParams = new URLSearchParams(window.location.search);

  return (
    window.location.pathname === "/reset-password" ||
    hashParams.get("type") === "recovery" ||
    queryParams.get("type") === "recovery"
  );
}

function App() {
  const [session, setSession] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [startupTimedOut, setStartupTimedOut] = useState(false);
  const [loginRedirectPath, setLoginRedirectPath] = useState(null);
  const [authMode, setAuthMode] = useState(
    isPasswordRecoveryUrl() ? "passwordRecovery" : "login"
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setStartupTimedOut(true);
    }, 12000);

    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (authLoading || session || isPasswordRecoveryUrl()) return undefined;
    const target = getProtectedRouteTarget(window.location.pathname, window.location.search, window.location.hash);
    if (!target || window.location.pathname === "/login") return undefined;
    window.history.replaceState(null, "", buildLoginRedirect(target));
    return undefined;
  }, [authLoading, session]);

  useEffect(() => {
    let mounted = true;

    async function loadSession() {
      try {
        const sessionPromise = supabase.auth.getSession();
        const timeoutPromise = new Promise((_, reject) => {
          window.setTimeout(() => {
            reject(new Error("Supabase auth session request timed out"));
          }, 10000);
        });

        const { data, error } = await Promise.race([
          sessionPromise,
          timeoutPromise,
        ]);

        if (!mounted) return;

        if (error) {
          console.error("Auth session error:", error);
        }

        setSession(data?.session ?? null);

        if (isPasswordRecoveryUrl()) {
          setAuthMode("passwordRecovery");
        }
      } catch (error) {
        console.error("Auth session load failed:", error);
      } finally {
        if (mounted) {
          setAuthLoading(false);
        }
      }
    }

    loadSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (event === "PASSWORD_RECOVERY") {
        setAuthMode("passwordRecovery");
      }

      if (event === "SIGNED_OUT") {
        setAuthMode("login");
      }

      setSession(nextSession);
      setAuthLoading(false);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  function handlePasswordUpdated() {
    setAuthMode("login");
    setSession(null);
    window.history.replaceState(null, "", "/");
  }

  function handleLoginSuccess() {
    const target = getRedirectFromLocation(window.location.search, DEFAULT_POST_LOGIN_PATH);
    setLoginRedirectPath(target);
    window.history.replaceState(null, "", target);
  }

  if (authLoading) {
    if (startupTimedOut) {
      return <StartupFallback />;
    }

    return (
      <div style={{ maxWidth: 420, margin: "80px auto", padding: 24 }}>
        Loading Toren...
      </div>
    );
  }

  if (authMode === "passwordRecovery") {
    return (
      <Login
        initialMode="passwordRecovery"
        onPasswordUpdated={handlePasswordUpdated}
      />
    );
  }

  if (!session) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  const path = loginRedirectPath || window.location.pathname;

  if (path === "/admin/onboarding") {
    return <AdminOnboarding session={session} />;
  }

  if (path === "/admin/revenue") {
    return <AdminRevenue session={session} />;
  }

  if (path === "/admin/cai-implementation-studio") {
    return (
      <Suspense fallback={<AdminRouteLoading />}>
        <CaiImplementationStudio session={session} />
      </Suspense>
    );
  }

  if (path === "/admin/current-asset-intelligence") {
    return (
      <Suspense fallback={<AdminRouteLoading />}>
        <CurrentAssetIntelligenceBrowserSafe
          session={session}
          internalProjection={getCurrentAssetIntelligenceFixtureFromLocation()}
        />
      </Suspense>
    );
  }

  if (path === "/partner" || path === "/partner/revenue") {
    return <PartnerRevenueDashboard session={session} />;
  }

  return <Dashboard session={session} />;
}

export default App;
